// file: ./components/Hooks/useAvailableTimes.js
import { useEffect, useState } from 'react';
import { getAvailableTimeblocks } from '../../algorithm/getAvailableTimeblocks';
import { filterTimeblocksByMaxArrivals } from '../../algorithm/filters/maxArrivalsFilter.js';
import { filterTimeblocksByMaxGroups } from '../../algorithm/filters/maxGroupsFilter.js';
import { isDateAvailableWithTableCheck } from '../../algorithm/isDateAvailableWithTableCheck';
import { filterTimeblocksByStopTimes } from '../../algorithm/filters/timeFilter.js';
import { getChefsRecommendation } from '../chefsRecommendationHelper';
import { getYeatTimes } from '../Utils/YeatHelpers';

// Use CommonJS require to ensure compatibility with existing code
const { getAvailableTimeblocksWithTableCheck } = require('../../algorithm/simulateTableAssignment');

function formatDate(date) {
  if (!date) return '';
  const year = date.getFullYear();
  const month = (`0${date.getMonth() + 1}`).slice(-2);
  const day = (`0${date.getDate()}`).slice(-2);
  return `${year}-${month}-${day}`;
}

// Robust boolean parser for values that may come as string/boolean/undefined
function parseBool(value, fallback = true) {
  if (value === undefined || value === null) return fallback;
  if (typeof value === 'string') return value === 'true';
  return Boolean(value);
}

export default function useAvailableTimes({
  selectedDate,
  guests,
  restaurantData,
  reservations,
  giftcard,
}) {
  const [availableTimes, setAvailableTimes] = useState([]);
  const [chefsRecommendation, setChefsRecommendation] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [hasRegularTimes, setHasRegularTimes] = useState(false);
  const [hasYeatTimes, setHasYeatTimes] = useState(false);
  // No explicit state for waitlist; we derive and display them alongside

  // Log key prop changes for debugging
  useEffect(() => {
    console.log(`TimeSelector props changed - selectedDate: ${selectedDate}, guests: ${guests}, giftcard: ${giftcard || 'none'}`);
  }, [selectedDate, guests, giftcard]);

  useEffect(() => {
    if (!selectedDate || !guests || guests <= 0 || !restaurantData) {
      setAvailableTimes([]);
      setChefsRecommendation(null);
      setHasRegularTimes(false);
      setHasYeatTimes(false);
      return;
    }

    setIsLoading(true);

    const timeoutId = setTimeout(() => {
      try {
        const dateStr = formatDate(selectedDate);
        console.log(`TimeSelector: Processing times for ${dateStr}`);

        // Read the new toggle from restaurant data (support both flattened and nested shapes).
        const rawChefPref =
          restaurantData?.['general-settings']?.chefTimeRecommendations ??
          restaurantData?.chefTimeRecommendations;
        const chefRecsEnabled = parseBool(rawChefPref, true); // default to true when unset/null
        console.log(`Chef's recommendation enabled: ${chefRecsEnabled}`);

        let regularTimes = [];
        let waitlistTimes = [];
        let hasRegular = false;

        // Check if regular availability exists
        const regularAvailable = isDateAvailableWithTableCheck(
          restaurantData,
          dateStr,
          reservations,
          guests,
          giftcard
        );

        if (regularAvailable) {
          console.log(`TimeSelector: Date ${dateStr} has regular availability, processing regular times`);
          hasRegular = true;

          const tableSettings = restaurantData?.['table-settings'] || {};
          const isTableAssignmentEnabled = tableSettings.isInstalled === true && tableSettings.assignmentMode === 'automatic';
          console.log(`TimeSelector: Table assignment is ${isTableAssignmentEnabled ? 'ENABLED' : 'DISABLED'}`);

          let availableTimeblocks = {};
          if (giftcard) {
            availableTimeblocks = getAvailableTimeblocks(
              restaurantData,
              dateStr,
              reservations,
              guests,
              giftcard
            );
          } else {
            availableTimeblocks = getAvailableTimeblocks(
              restaurantData,
              dateStr,
              reservations,
              guests
            );
          }

          console.log(`Initial available timeblocks: ${Object.keys(availableTimeblocks).length}`);

          if (Object.keys(availableTimeblocks).length > 0) {
            const groupsFilteredTimeblocks = filterTimeblocksByMaxGroups(
              restaurantData,
              dateStr,
              availableTimeblocks,
              reservations,
              guests
            );
            console.log(`After max groups filter: ${Object.keys(groupsFilteredTimeblocks).length} timeblocks`);

            if (Object.keys(groupsFilteredTimeblocks).length > 0) {
              const arrivalsFilteredTimeblocks = filterTimeblocksByMaxArrivals(
                restaurantData,
                dateStr,
                groupsFilteredTimeblocks,
                reservations,
                guests
              );
              console.log(`After max arrivals filter: ${Object.keys(arrivalsFilteredTimeblocks).length} timeblocks`);

              if (Object.keys(arrivalsFilteredTimeblocks).length > 0) {
                let finalTimeblocks = arrivalsFilteredTimeblocks;

                if (isTableAssignmentEnabled) {
                  console.log('Table assignment is ENABLED, checking table availability');
                  const tableFilteredTimeblocks = getAvailableTimeblocksWithTableCheck(
                    restaurantData,
                    dateStr,
                    finalTimeblocks,
                    guests,
                    reservations
                  );
                  console.log(`After table check: ${Object.keys(tableFilteredTimeblocks).length} timeblocks`);

                  finalTimeblocks = filterTimeblocksByMaxArrivals(
                    restaurantData,
                    dateStr,
                    tableFilteredTimeblocks,
                    reservations,
                    guests
                  );
                }

                const stopTimeFilteredTimeblocks = filterTimeblocksByStopTimes(
                  finalTimeblocks,
                  restaurantData,
                  dateStr
                );
                console.log(`After stop time filter: ${Object.keys(stopTimeFilteredTimeblocks).length} timeblocks`);

                regularTimes = Object.keys(stopTimeFilteredTimeblocks).map((timeKey) => ({
                  value: timeKey,
                  label: stopTimeFilteredTimeblocks[timeKey].name || timeKey,
                  isYeat: false,
                }));
              }
            }
          }
        }

        // Derive waitlist times: simulate baseline availability with NO reservations
        try {
          const idealBaseTimeblocks = getAvailableTimeblocks(
            restaurantData,
            dateStr,
            [],
            guests,
            giftcard
          );
          if (Object.keys(idealBaseTimeblocks).length > 0) {
            const idealAfterGroups = filterTimeblocksByMaxGroups(
              restaurantData,
              dateStr,
              idealBaseTimeblocks,
              [],
              guests
            );
            if (Object.keys(idealAfterGroups).length > 0) {
              const idealAfterArrivals = filterTimeblocksByMaxArrivals(
                restaurantData,
                dateStr,
                idealAfterGroups,
                [],
                guests
              );
              let idealFinal = idealAfterArrivals;
              if ((restaurantData?.['table-settings'] || {}).isInstalled === true && (restaurantData?.['table-settings'] || {}).assignmentMode === 'automatic') {
                const idealAfterTable = getAvailableTimeblocksWithTableCheck(
                  restaurantData,
                  dateStr,
                  idealFinal,
                  guests,
                  []
                );
                idealFinal = idealAfterTable;
              }
              const idealAfterStop = filterTimeblocksByStopTimes(
                idealFinal,
                restaurantData,
                dateStr
              );
              const regularSet = new Set(regularTimes.map(t => t.value));
              waitlistTimes = Object.keys(idealAfterStop)
                .filter(timeKey => !regularSet.has(timeKey))
                .map(timeKey => ({
                  value: timeKey,
                  label: `${idealAfterStop[timeKey].name || timeKey} - Wachtlijst`,
                  isYeat: false,
                  isWaitlist: true,
                }));
            }
          }
        } catch (e) {
          console.warn('Failed to compute waitlist baseline times:', e);
        }

        const yeatTimes = getYeatTimes(reservations, dateStr, guests);
        const hasYeat = yeatTimes.length > 0;

        console.log(`Found ${regularTimes.length} regular times and ${yeatTimes.length} Yeat times`);

        let allTimes = [];
        allTimes.push(...regularTimes);
        yeatTimes.forEach((yeatTime) => {
          const conflictingRegularTime = regularTimes.find((regularTime) => regularTime.value === yeatTime.value);
          if (!conflictingRegularTime) {
            allTimes.push(yeatTime);
          } else {
            console.log(`Skipping Yeat time ${yeatTime.value} as regular time exists`);
          }
        });
        // Append waitlist times that are not already included
        const existingTimeValues = new Set(allTimes.map(t => t.value));
        waitlistTimes.forEach(w => {
          if (!existingTimeValues.has(w.value)) {
            allTimes.push(w);
          }
        });

        allTimes.sort((a, b) => a.value.localeCompare(b.value));

        // Apply Chef's Aanrader only if enabled (unset or true => enabled)
        let recommendedTime = null;
        if (regularTimes.length > 0 && chefRecsEnabled) {
          recommendedTime = getChefsRecommendation(regularTimes);
          setChefsRecommendation(recommendedTime);

          allTimes = allTimes.map((time) => {
            if (!time.isYeat && time.value === recommendedTime) {
              return {
                ...time,
                label: time.label.includes("Chef's Aanrader") ? time.label : `${time.label} Chef's Aanrader`,
              };
            }
            return time;
          });
        } else {
          // Disabled or no regular times: ensure no recommendation label/info is shown
          setChefsRecommendation(null);
          // No relabeling when disabled
        }

        setHasRegularTimes(hasRegular && regularTimes.length > 0);
        setHasYeatTimes(hasYeat);

        if (allTimes.length > 0) {
          const regularTimesList = allTimes
            .filter((t) => !t.isYeat)
            .map((t) => t.value)
            .join(', ');
          const yeatTimesList = allTimes
            .filter((t) => t.isYeat)
            .map((t) => `${t.value}(${t.lastMinuteId || 'no-id'})`)
            .join(', ');
          console.log(`Available regular times: ${regularTimesList || 'none'}`);
          console.log(`Available Yeat times: ${yeatTimesList || 'none'}`);
        } else {
          console.log('No available times after all filtering');
        }

        setAvailableTimes(allTimes);
      } catch (error) {
        console.error('Error fetching available times:', error);
        setAvailableTimes([]);
        setChefsRecommendation(null);
        setHasRegularTimes(false);
        setHasYeatTimes(false);
      } finally {
        setIsLoading(false);
      }
    }, 50);

    return () => clearTimeout(timeoutId);
  }, [selectedDate, guests, restaurantData, reservations, giftcard]);

  return {
    availableTimes,
    chefsRecommendation,
    isLoading,
    hasRegularTimes,
    hasYeatTimes,
  };
}
